/**
 * Client HTTP de l'API /api/saves.
 *
 * Le serveur est agnostique de la forme du blob — c'est le front qui est
 * propriétaire. Ici on fixe le contrat `SaveBlob` : plan_id + plan_version
 * (pour invalider les saves incompatibles après MAJ), archetype_id, seed
 * et `decisions_history` (la source de vérité déterministe).
 */

export interface SaveBlob {
  /** Version du schéma du blob (pour migrations futures). */
  version: 1;
  plan_id: string;
  plan_version: string;
  archetype_id: string;
  seed: number;
  /** Tous les `applyTurn` joués depuis le début : une entrée par tour. */
  decisions_history: string[][];
  /**
   * Historique des archétypes proposés au joueur — une entrée par tour
   * affiché. Index 0 = suggestion initiale au boot, index N = suggestion
   * après avoir joué le tour N. `null` = aucune suggestion (écran pause).
   *
   * Optional pour rétro-compat : les saves pré-existantes n'ont pas ce
   * champ et on retombe sur `engine.suggestedNext()` lors du boot.
   */
  presented_history?: (string | null)[];
  /**
   * H4 — feed de presse cumulé (events tirés + initial). Persisté pour
   * que le ticker ne reparte pas de zéro après refresh. Optionnel pour
   * rétro-compat avec les saves antérieures à 2026-05-04.
   */
  headline_feed?: string[];
  /** Numéro de tour courant — redondant avec `decisions_history.length` mais utile pour debug. */
  current_turn: number;
}

const API_BASE = "/api/saves";

/**
 * Flag localStorage qui mémorise si une save a déjà été poussée au
 * serveur pour la session courante. Évite de fetchSave inutilement
 * (et donc d'afficher un 404 dans la console réseau du navigateur)
 * pour une session fraîche qui n'a jamais sauvegardé.
 */
const HAS_SAVE_KEY = "mp_has_remote_save";

export function hasKnownRemoteSave(): boolean {
  try {
    return localStorage.getItem(HAS_SAVE_KEY) === "true";
  } catch {
    return false;
  }
}

function markRemoteSaveExists(): void {
  try {
    localStorage.setItem(HAS_SAVE_KEY, "true");
  } catch {
    /* ignore */
  }
}

export function clearRemoteSaveFlag(): void {
  try {
    localStorage.removeItem(HAS_SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** Récupère la sauvegarde, ou null si absente / erreur réseau. */
export async function fetchSave(sessionId: string): Promise<SaveBlob | null> {
  // Évite le 404 inutile (et bruyant en console) quand on sait que
  // la session n'a jamais poussé de save. Le serveur reste authoritative
  // — on ne fait que zapper un round-trip inutile.
  if (!hasKnownRemoteSave()) return null;
  try {
    const res = await fetch(`${API_BASE}/${sessionId}`, {
      method: "GET",
      credentials: "same-origin",
    });
    if (res.status === 404) {
      // La save a été supprimée côté serveur (clean-up, autre device,
      // etc.) — on resync le flag local pour éviter de re-tenter.
      clearRemoteSaveFlag();
      return null;
    }
    if (!res.ok) {
      console.warn(`[saves] GET failed with ${res.status}`);
      return null;
    }
    const json = (await res.json()) as SaveBlob;
    return json;
  } catch (e) {
    console.warn("[saves] GET network error", e);
    return null;
  }
}

/** Upsert fire-and-forget — les erreurs sont logées mais ne bloquent jamais le jeu. */
export async function pushSave(
  sessionId: string,
  blob: SaveBlob,
): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/${sessionId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(blob),
    });
    if (!res.ok) {
      console.warn(`[saves] PUT failed with ${res.status}`);
      return;
    }
    // Marque la save comme existante côté local — au prochain boot,
    // fetchSave sera autorisé à tenter le GET (sinon il skip).
    markRemoteSaveExists();
  } catch (e) {
    console.warn("[saves] PUT network error", e);
  }
}

/** Supprime le blob côté serveur — usage : reset partie. */
export async function deleteSave(sessionId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/${sessionId}`, {
      method: "DELETE",
      credentials: "same-origin",
    });
  } catch (e) {
    console.warn("[saves] DELETE network error", e);
  } finally {
    // Idempotent : reset le flag même si le DELETE a échoué — l'état
    // local devient « pas de save » immédiatement.
    clearRemoteSaveFlag();
  }
}
